import React, {Dispatch, SetStateAction, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import clsx from 'clsx';
import {SelectionRow, clearSelection} from 'src/components/data-table-selection';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useExpandRoles} from 'src/modules/entitlements';
import {
    MainColumnAssignedResourceCell,
    MainColumnFreeResourceCell,
    MainColumnGroupCell,
    MainColumnHeader,
} from 'src/modules/entitlements/components/main-column';
import {mainColumnCellGroupRow} from 'src/modules/entitlements/components/main-column/main-column-cell.module.styl';
import {RelationLevel as RelationLevels} from 'src/openapi/admin/models/RelationLevel';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Cell, Column, DataTable, HeaderCellOptions, TableHeader} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {RadioButton} from '@pb/kit-component-radio-button';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {RemoveEntitlementTool} from './components/remove-entitlement-tool';
import {COLUMNS, COLUMNS_DATA, HEADERS, SELECTION_KEY} from './constants';
import {useGetPresetEntitlements} from './hooks/useGetPresetEntitlements';

import {preparedEntitlementsTools} from './prepared-entitlements.module.styl';

import type {CellOptions, RowsParams} from '@pb/kit-component-data-table-v2';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {RelationLevel} from 'src/openapi/admin/models/RelationLevel';

type Props = {
    relations: PresetRelationItem[],
    onChange: Dispatch<SetStateAction<PresetRelationItem[]>>,
};

export const PresetEntitlementsTable = (props: Props) => {
    const {relations, onChange} = props;
    const dispatch = useDispatch();

    const {resourceViews} = useGetPresetEntitlements({relations});
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {expandedRoleIds, setExpandedRoleIds} = useExpandRoles({records: resourceViews});

    const renderedRows = useMemo(() => (resourceViews
        .filter(entitlement => {
            const record = JSON.parse(entitlement.recordId);
            return (record.roleId && record.resourceId
                ? expandedRoleIds.includes(record.roleId)
                : true
            );
        })
        .map(entitlement => ({
            id: entitlement.recordId,
            columnValues: {
                title: entitlement.title,
                description: entitlement.description,
                grant: entitlement.grantTitle ?? '',
                status: entitlement.statusTitle ?? '',
                restriction: entitlement.restrictionTitle ?? '',
                userLevel: entitlement.level === RelationLevels.CHILD,
                accountLevel: entitlement.level === RelationLevels.PARENT,
            },
        }))
    ), [resourceViews, expandedRoleIds]);

    const selectableRows = useMemo(() => (renderedRows
        .filter(row => {
            const record = JSON.parse(row.id);
            return record.roleId ? !record.resourceId : true;
        })
        .map(({id}) => id) || []
    ), [renderedRows]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (isDeepEqual({prev, current})) {
            dispatch(clearSelection(SELECTION_KEY));
        }
    }, relations);

    return relations.length ? (
        <div>
            <StickyElement className={`sticky-tool-ribbon ${preparedEntitlementsTools}`}>
                <ToolsRibbonComponent {...{
                    rows: selectableRows,
                    selectionKey: SELECTION_KEY,
                    showSelectAll: false,
                    totalCount: selectableRows.length,
                }}>
                    {ids => (
                        <RemoveEntitlementTool
                            selectedRecords={ids as string[]}
                            onChange={onChange}
                        />
                    )}
                </ToolsRibbonComponent>
            </StickyElement>
            <DataTable
                id="create-preset-entitlements"
                data={{
                    rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                        ...column,
                        width: getColumnWidthByColumnId(column.columnId),
                    })),
                }}
                getHeaderTopOffset={() => 152}
                onStopResize={onStopResize}
                headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                    switch (column.columnId) {
                        case COLUMNS.TITLE:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <MainColumnHeader selection={<div/>}>
                                        <b>Role/Resource</b>
                                    </MainColumnHeader>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <TableHeader indent="none" className="search-header">
                                        <Group alignItems="center">
                                            <strong>{HEADERS[column.columnId]}</strong>
                                        </Group>
                                    </TableHeader>
                                </Cell>
                            );
                    }
                }}
                renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                    const {rowIndex, column: {columnId}} = rowsParams;
                    const value = rowsParams.row.columnValues[columnId] as string | boolean;
                    const column = rowsParams.column;
                    const rowId = rowsParams.row.id;
                    const cellKey = `${rowId}-${columnId}`;

                    const record = JSON.parse(rowId as string);

                    const isGroupRow = record.roleId && !record.resourceId;
                    const roleId = isGroupRow ? record.roleId : undefined;
                    const grantId = record.grantId;
                    const isAssignedResource = !!(record.roleId && record.resourceId);

                    const cellClasses = clsx('search-result-cell', [
                        isGroupRow && mainColumnCellGroupRow,
                    ]);

                    const onLevelClick = (level: RelationLevel) => {
                        if (isGroupRow) {
                            onChange(prev => prev.map(relation => {
                                if (relation.roleId === roleId) {
                                    return {...relation, level};
                                }

                                return relation;
                            }));
                        } else if (!isAssignedResource) {
                            onChange(prev => prev.map(relation => {
                                if (relation.grantId === grantId && !relation.roleId) {
                                    return {...relation, level};
                                }

                                return relation;
                            }));
                        }
                    };

                    switch (columnId) {
                        case COLUMNS.TITLE:
                            if (isGroupRow) {
                                return (
                                    <MainColumnGroupCell
                                        key={cellKey}
                                        {...{
                                            selection: (
                                                <SelectionRow
                                                    rows={selectableRows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={SELECTION_KEY}
                                                    totalCount={selectableRows.length}
                                                />
                                            ),
                                            isExpanded: expandedRoleIds.includes(roleId as string),
                                            onExpand: () => setExpandedRoleIds(
                                                prev => [...prev, roleId as string],
                                            ),
                                            onCollapse: () => setExpandedRoleIds(
                                                prev => prev.filter(id => id !== roleId),
                                            ),
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <EllipsisTextHint title={String(value)}>
                                            <strong>{String(value)}</strong>
                                        </EllipsisTextHint>
                                    </MainColumnGroupCell>
                                );
                            }

                            if (isAssignedResource) {
                                return (
                                    <MainColumnAssignedResourceCell
                                        key={cellKey}
                                        {...{cellOptions: {...cellOptions, column}}}>
                                        <EllipsisTextHint title={String(value)}>
                                            {String(value)}
                                        </EllipsisTextHint>
                                    </MainColumnAssignedResourceCell>
                                );
                            }

                            return (
                                <MainColumnFreeResourceCell
                                    key={cellKey}
                                    {...{
                                        selection: (
                                            <SelectionRow
                                                rows={selectableRows}
                                                index={rowIndex}
                                                entityId={rowId}
                                                selectionKey={SELECTION_KEY}
                                                totalCount={selectableRows.length}
                                            />
                                        ),
                                        cellOptions: {...cellOptions, column},
                                        groupRow: isGroupRow,
                                    }}>
                                    <div style={{paddingLeft: isAssignedResource ? 32 : 0}}>
                                        <EllipsisTextHint title={String(value)}>
                                            {String(value)}
                                        </EllipsisTextHint>
                                    </div>
                                </MainColumnFreeResourceCell>
                            );
                        case COLUMNS.GRANT:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={String(value)}>
                                        {String(value)}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                        case COLUMNS.ACCOUNT_LEVEL:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    {isAssignedResource ? <span/> : (
                                        <span onClick={() => onLevelClick(RelationLevels.PARENT)}>
                                            <RadioButton
                                                showLabel={false}
                                                value={RelationLevels.PARENT}
                                                checked={value as boolean}
                                            />
                                        </span>
                                    )}
                                </Cell>
                            );
                        case COLUMNS.USER_LEVEL:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    {isAssignedResource ? <span/> : (
                                        <span onClick={() => onLevelClick(RelationLevels.CHILD)}>
                                            <RadioButton
                                                showLabel={false}
                                                value={RelationLevels.CHILD}
                                                checked={value as boolean}
                                            />
                                        </span>
                                    )}
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={String(value)}>
                                        {String(value)}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                    }
                }}
            />
        </div>
    ) : null;
};
